/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useMemo } from "react";
import { CardHeader } from "@mui/material";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { alpha, useTheme } from "@mui/material/styles";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import styled from "@emotion/styled";
import { isDemoEnvironment } from "../utils/utils";

const ChartContainer = styled.div`
  height: 250px;
`;

interface AutomaticAndManualLineChartProps {
  writtenChanges: any[];
  systemType?: string;
}

const chartOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        color: "rgba(0,0,0,0.0)",
      },
    },
    y: {
      grid: {
        color: "rgba(0,0,0,0.0375)",
        fontColor: "#fff",
      },
      beginAtZero: true,
    },
  },
};

const generateDemoData = () => {
  const labels: string[] = [];
  const automatedData: number[] = [];
  const manualData: number[] = [];

  const now = new Date();

  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(
      date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    );

    const monthIndex = 11 - i;
    const baseAutomated = 800 + monthIndex * 340;
    const baseManual = 200 + monthIndex * 70;

    const randomVariation = () => Math.floor(Math.random() * 100) - 50;

    automatedData.push(baseAutomated + randomVariation());
    manualData.push(baseManual + randomVariation());
  }

  return { labels, automatedData, manualData };
};

export const AutomaticAndManualLineChart: React.FC<AutomaticAndManualLineChartProps> =
  ({ writtenChanges, systemType }) => {
    const theme = useTheme();

    const demoData = useMemo(() => {
      if (isDemoEnvironment() && systemType === "INTERACTION") {
        return generateDemoData();
      }
      return null;
    }, [systemType]);

    const processChartData = () => {
      let labels: string[];
      let automatedData: number[];
      let manualData: number[];

      if (demoData) {
        labels = demoData.labels;
        automatedData = demoData.automatedData;
        manualData = demoData.manualData;
      } else {
        if (!writtenChanges || writtenChanges.length === 0) {
          return { labels: [], datasets: [] };
        }

        const sortedData = [...writtenChanges].sort(
          (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
        );

        labels = sortedData.map((item) => {
          const date = new Date(item.month);
          return date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
        });

        manualData = sortedData.map((item) => item.manual_approved_count || 0);
        automatedData = sortedData.map(
          (item) =>
            (item.approved_total || 0) - (item.manual_approved_count || 0)
        );
      }

      return {
        labels,
        datasets: [
          {
            label: "Automated Changes",
            fill: true,
            backgroundColor: function (context: any) {
              const chart = context.chart;
              const { ctx, chartArea } = chart;

              if (!chartArea) {
                return null;
              }

              const gradient = ctx.createLinearGradient(0, 0, 0, 300);
              gradient.addColorStop(
                0,
                alpha(theme?.palette.primary.main, 0.0875)
              );
              gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

              return gradient;
            },
            borderColor: theme?.palette.primary.main,
            tension: 0.4,
            data: automatedData,
          },
          {
            label: "Manual Changes",
            fill: true,
            backgroundColor: "transparent",
            borderColor: theme?.palette.grey[500],
            borderDash: [4, 4],
            tension: 0.4,
            data: manualData,
          },
        ],
      };
    };

    const lineData = processChartData();

    return (
      <MiraCard sx={{ height: "100%" }}>
        <CardHeader title={"Automated and Manual Changes"} />
        <MiraCardContent>
          <ChartContainer>
            <Line data={lineData} options={chartOptions} />
          </ChartContainer>
        </MiraCardContent>
      </MiraCard>
    );
  };
